import * as React from 'react';
import styled from 'styled-components';
// import { TweenMax } from 'gsap';
import { Cookies, withCookies } from 'react-cookie';
import { instanceOf } from 'prop-types';

// Components
import TextBlock from '../TextBlock';
import Hyperlink from '../Hyperlink';

// Assets
import { brandColors, fonts } from '../../styles/variables';

const Wrapper = styled.div`
  background-color: ${brandColors.orange};
  position: fixed;
  left: 0px;
  bottom: 0px;
  width: 100%;
  max-width: 800px;
  z-index: 9999;
  box-sizing: border-box;
  padding: 17px;
`;

const Content = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;

  .text-block {
    max-width: 80%;
    display: inline-block;
    vertical-align: bottom;

    h3 {
      margin: 0;
      font-style: normal;
      font-family: ${fonts.monterrat};
    }

    p {
      font-size: 16px;
      line-height: 22px;
      margin: 14px 0;
    }
  }
`;

const AcknowledgementButtons = styled.div`
  margin: 10px 0 0 0;
`;

const BaseBtn = styled.div`
  width: 100%;
  max-width: 20%;
  display: inline-block;
  vertical-align: bottom;

  div {
    width: 140px;
    border: 1px solid;
    text-align: center;
    cursor: pointer;
    padding: 13px;

    p {
      letter-spacing: 2px;
      font-family: ${fonts.monterrat};
      line-height: 15px;
      text-transform: uppercase;
      font-size: 11px;
      margin: 6px 0;
    }
  }
`;

const AcceptButton = styled(BaseBtn)`
  div {
    border-color: ${brandColors.blue};
    background: ${brandColors.blue};
  }

  p {
    color: ${brandColors.white};
  }
`;

const RejectButton = styled(BaseBtn)`
  div {
    border-color: ${brandColors.black};
  }

  p {
    color: ${brandColors.black};
  }
`;

const ReopenLink = styled.a`
  position: fixed;
  bottom: 0;
  left: 0;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
  z-index: 9998;
  background: ${brandColors.orange};
  padding: 10px;
  color: ${brandColors.blue};
  text-decoration: none;
  font-family: ${fonts.monterrat};

  &:hover {
    text-decoration: none;
  }
`;

class CookieBanner extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isVisible: true
    };

    this.handleChoice = this.handleChoice.bind(this);
    this.triggerReset = this.triggerReset.bind(this);
    this.updateGoogleConsent = this.updateGoogleConsent.bind(this);
    this.clearNonEssentialCookies = this.clearNonEssentialCookies.bind(this);
  }

  componentDidMount() {
    const { cookies } = this.props;
    const consent = cookies.get('cookieConsent');
    if (consent) {
      this.setState({ isVisible: false });
      this.updateGoogleConsent(consent);
    }
  }

  clearNonEssentialCookies() {
    if (typeof document === 'undefined') return;
    const { cookies } = this.props;
    const allCookies = cookies.getAll();
    const cookieKeys = Object.keys(allCookies);

    const { hostname } = window.location;
    const domainParts = hostname.split('.');
    const mainDomain =
      domainParts.length > 2 ? `.${domainParts.slice(-2).join('.')}` : `.${hostname}`;
    const domainsToClear = [hostname, mainDomain, `.${hostname}`, ''];

    for (let i = 0; i < cookieKeys.length; i++) {
      const cookieName = cookieKeys[i];
      if (cookieName !== 'cookieConsent') {
        domainsToClear.forEach(domain => {
          const domainString = domain ? `; domain=${domain}` : '';

          document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0${domainString};`;
          document.cookie = `${cookieName}=; path=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0${domainString};`;
        });
      }
    }
  }

  updateGoogleConsent(choice) {
    const { window: win } = typeof window !== 'undefined' ? { window } : { window: {} };

    if (win && typeof win.gtag === 'function') {
      const status = choice === 'accepted' ? 'granted' : 'denied';

      win.gtag('consent', 'update', {
        analytics_storage: status,
        ad_storage: status,
        ad_user_data: status,
        ad_personalization: status,
        functionality_storage: status,
        personalization_storage: status
      });

      if (choice === 'rejected') {
        setTimeout(() => {
          this.clearNonEssentialCookies();
        }, 100);
      }
    }
  }

  handleChoice(choice) {
    const { cookies } = this.props;
    const date = new Date();
    date.setTime(date.getTime() + 7 * 24 * 60 * 60 * 1000);

    cookies.set('cookieConsent', choice, {
      path: '/',
      expires: date,
      secure: true,
      sameSite: 'lax'
    });

    this.setState({ isVisible: false });
    this.updateGoogleConsent(choice);
    window.location.reload();
  }

  triggerReset(e) {
    e.preventDefault();
    this.setState({ isVisible: true });
  }

  render() {
    const { isVisible } = this.state;
    const { handleChoice, triggerReset } = this;

    return (
      <div>
        {isVisible && (
          <Wrapper innerRef={this.banner}>
            <Content>
              <TextBlock className="text-block">
                <h3>Cookies</h3>
                <p>
                  This website uses cookies to ensure you get the best experience on our website.
                </p>
                <p>
                  For more information, including details on how to change your cookie settings,
                  please read our{' '}
                  <Hyperlink
                    linkName="cookie policy"
                    linkAddress="/cookies"
                    internalLink
                    constantLink
                  />. By clicking “Accept all”, you consent to the use of ALL the cookies.
                </p>
              </TextBlock>

              <AcknowledgementButtons>
                <AcceptButton onClick={() => handleChoice('accepted')}>
                  <div>
                    <p>Accept all</p>
                  </div>
                </AcceptButton>
                <RejectButton onClick={() => handleChoice('rejected')}>
                  <div>
                    <p>Reject all</p>
                  </div>
                </RejectButton>
              </AcknowledgementButtons>
            </Content>
          </Wrapper>
        )}
        {!isVisible && (
          <ReopenLink href="#cookies" onClick={triggerReset}>
            Cookie Settings
          </ReopenLink>
        )}
      </div>
    );
  }
}

CookieBanner.propTypes = {
  cookies: instanceOf(Cookies).isRequired
};

export default withCookies(CookieBanner);



// WEBPACK FOOTER //
// ./src/components/CookieBanner/index.jsx